<template>
    <div class="resize-settings-template-section">
        <div v-if="showTemplateSpecificSettingsSwitch || showLabel" class="resize-settings-template-section__title-row">
            <h3 v-if="showLabel" class="resize-settings-template-section__label">
                {{ label }}
            </h3>
            <div v-if="showTemplateSpecificSettingsSwitch">
                <LabeledSwitch v-model="hasOwnResizeSettings">Template specific settings</LabeledSwitch>
            </div>
        </div>

        <ResizeSettings
            v-if="hasOwnResizeSettings"
            v-model:resize-type="internalResizeType"
            :resize-dimensions="internalResizeDimensions"
            @dimensionChange="onInputChange"
        />

        <div class="resize-settings-template-section__preview-container">
            <ImageCropper
                v-if="showManualResizeComponent"
                :image-crop-coordinates="resizeSettings.manualSettings"
                :image-url="previewImageUrl"
                :require-click-to-enable-zoom="internalRequireClickToEnableImageCroppingZoom"
                :viewport-height-pixels="internalResizeDimensions.height"
                :viewport-width-pixels="internalResizeDimensions.width"
                @cropCoordinatesUpdated="setManualSettings"
            />
            <AssetPreview v-else :asset-url="resizedPreviewImageUrl" />
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";

import {
    CoverType,
    HorizontalAlign,
    ResizeType,
    VerticalAlign,
    constructCoverUrl
} from "shared-utils/utils/templateCover";

import LabeledSwitch from "@/components/Campaign/LabeledSwitch.vue";
import ResizeSettingsComponent from "@/components/ResizeSettings.vue";
import ImageCropper from "@/components/common/ImageCropper.vue";
import AssetPreview from "@/components/common/AssetPreview.vue";

import { getBaseApiUrl } from "@/utils/CreativeIntelligenceApi";
import type { Dimensions, NestedResizeSettings, ResizeSettings } from "shared-types";

export default defineComponent({
    components: {
        ImageCropper,
        AssetPreview,
        LabeledSwitch,
        ResizeSettings: ResizeSettingsComponent
    },
    props: {
        initialHasOwnResizeSettings: {
            type: Boolean
        },
        groupResizeType: {
            required: true,
            validator(value: (typeof ResizeType)[keyof typeof ResizeType]) {
                return Object.values(ResizeType).includes(value);
            },
            type: String as PropType<(typeof ResizeType)[keyof typeof ResizeType]>
        },
        groupResizeSettings: {
            required: true,
            type: Object as PropType<NestedResizeSettings>
        },
        label: {
            required: true,
            type: String
        },
        previewImageUrl: {
            required: true,
            type: String
        },
        requireClickToEnableImageCroppingZoom: {
            type: Boolean
        },
        resizeDimensions: {
            required: true,
            validator(value: Partial<Dimensions>) {
                return value.height !== undefined && value.width !== undefined;
            },
            type: Object as PropType<Dimensions>
        },
        showLabel: {
            default: true,
            type: Boolean
        },
        showTemplateSpecificSettingsSwitch: {
            default: true,
            type: Boolean
        },
        templateId: {
            required: true,
            type: String
        },
        templateResizeSettings: {
            type: Object as PropType<ResizeSettings>
        },
        manualDimensions: {
            type: Object as PropType<Dimensions | null>
        }
    },

    emits: ["removeTemplateResizeSettings", "setTemplateResizeSettings"],

    data() {
        /*
            The primary reason why we have internalResizeSettings and internalResizeType
            is so that the state persists if the user toggles hasOwnResizeSettings
            off and on again.
        */
        return {
            hasOwnResizeSettings: this.templateResizeSettings !== undefined,
            hasSetOwnResizeType: false,
            internalResizeDimensions: {} as Dimensions,
            internalResizeSettings: this.templateResizeSettings ?? ({} as ResizeSettings),
            internalResizeType: ResizeType.Off as (typeof ResizeType)[keyof typeof ResizeType],
            shouldTakeGroupResizeType: this.templateResizeSettings === undefined
        };
    },
    computed: {
        showManualResizeComponent() {
            return (
                (this.internalResizeType === ResizeType.Manual && this.hasOwnResizeSettings) ||
                this.groupResizeType === ResizeType.Manual
            );
        },
        internalRequireClickToEnableImageCroppingZoom() {
            return this.requireClickToEnableImageCroppingZoom && !this.hasSetOwnResizeType;
        },
        resizedPreviewImageUrl() {
            if (
                !this.internalResizeDimensions ||
                !this.resizeIsEnabled ||
                (!this.resizeSettings?.cover && !this.resizeSettings?.manualSettings)
            ) {
                return this.previewImageUrl;
            }

            return constructCoverUrl({
                imageUrlToResize: this.previewImageUrl,
                manualSettings: this.resizeSettings?.manualSettings,
                renderService: getBaseApiUrl()
            });
        },
        resizeIsEnabled() {
            return this.resizeIsEnabledWithGroupSettings || this.resizeIsEnabledWithInternalSettings;
        },
        resizeIsEnabledWithGroupSettings() {
            return this.groupResizeType !== ResizeType.Off && !this.hasOwnResizeSettings;
        },
        resizeIsEnabledWithInternalSettings() {
            return this.internalResizeType !== ResizeType.Off && this.hasOwnResizeSettings;
        },
        resizeSettings() {
            return this.templateResizeSettings || this.groupResizeSettings;
        }
    },
    watch: {
        hasOwnResizeSettings() {
            if (!this.hasOwnResizeSettings) {
                this.$emit("removeTemplateResizeSettings");
            } else {
                /*
                    this.shouldTakeGroupResizeType will be true if this
                    component was initialised without its own template resize
                    settings.

                    So that the image preview doesn't instantly change we want
                    to take the resize type from the group so that the settings
                    we show for the template are the same as the settings we
                    have for the group.
                */
                if (this.shouldTakeGroupResizeType) {
                    this.shouldTakeGroupResizeType = false;
                    this.internalResizeType = this.groupResizeType;
                }
                if (this.internalResizeType === ResizeType.Off) {
                    /*
                        While this should normally be handled by the internalResizeType
                        watcher it's possible that it would never get triggered
                        if internalResizeType is set to the same value it previously
                        was.
                    */
                    this.$emit("setTemplateResizeSettings", {});
                } else {
                    /*
                        This is to ensure that values that the user may have previously
                        set for this template are kept if they toggle hasOwnResizeSettings
                        off and on, falling back to the current groupResizeSettings if no
                        settings have previously been set for the template.
                    */
                    this.internalResizeSettings = {
                        cover: this.internalResizeSettings.cover || this.groupResizeSettings.cover,
                        horizontalAlign:
                            ("horizontalAlign" in this.internalResizeSettings &&
                                this.internalResizeSettings.horizontalAlign) ||
                            ("horizontalAlign" in this.groupResizeSettings &&
                                this.groupResizeSettings.horizontalAlign) ||
                            HorizontalAlign.Center,
                        manualSettings:
                            this.internalResizeSettings.manualSettings ||
                            this.groupResizeSettings.manualSettings ||
                            null,
                        verticalAlign:
                            ("verticalAlign" in this.internalResizeSettings &&
                                this.internalResizeSettings.verticalAlign) ||
                            ("verticalAlign" in this.groupResizeSettings && this.groupResizeSettings.verticalAlign) ||
                            VerticalAlign.Center
                    };
                }
            }
        },

        internalResizeType() {
            this.hasSetOwnResizeType = true;
            if (this.internalResizeType === ResizeType.Off) {
                this.$emit("setTemplateResizeSettings", {});
            } else if (this.internalResizeType === ResizeType.Manual) {
                this.internalResizeSettings = {
                    ...this.internalResizeSettings,
                    cover: CoverType.Manual
                };
            }
        },
        internalResizeSettings: {
            deep: true,
            handler() {
                this.$emit("setTemplateResizeSettings", { ...this.internalResizeSettings });
            }
        },

        manualDimensions: {
            deep: true,
            handler(val) {
                this.internalResizeDimensions = val;
            }
        }
    },

    created() {
        this.internalResizeDimensions = this.resizeDimensions;

        if (this.templateResizeSettings?.cover !== CoverType.Manual) {
            this.internalResizeType = ResizeType.Manual;

            if (this.resizeSettings.manualSettings) {
                this.internalResizeDimensions = {
                    width: this.resizeSettings.manualSettings.width,
                    height: this.resizeSettings.manualSettings.height
                };
            }
        }

        if (this.initialHasOwnResizeSettings && this.showTemplateSpecificSettingsSwitch) {
            /*
                If we are only showing resize settings for a single template then
                we want to default to showing the resize settings rather requiring
                the user to toggle "has own resize settings".
            */
            this.hasOwnResizeSettings = true;
        }
    },

    methods: {
        onInputChange(manualDimensions: Dimensions) {
            this.internalResizeDimensions = manualDimensions;
        },

        setManualSettings(imageCropCoordinates: {
            bottomRightCoordinates: { x: number; y: number };
            topLeftCoordinates: { x: number; y: number };
        }) {
            const path = new URL(this.previewImageUrl).pathname.replace(/^\//, "");

            this.internalResizeSettings = {
                ...this.internalResizeSettings,
                manualSettings: { path, ...this.internalResizeDimensions, ...imageCropCoordinates }
            };
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.resize-settings-template-section {
    border-bottom: 1px solid $grey3;
    margin: 0 0 $spacing;
    padding: 0 0 $spacing;

    &:last-child {
        border-bottom: none;
        padding-bottom: 0;
    }

    .new-size-modal__form {
        display: flex;
        justify-content: flex-end;
    }
}

.resize-settings-template-section__label {
    font-size: $font-size-large;
    font-weight: bold;
}

.resize-settings-template-section__preview-container {
    height: 240px;
}

.resize-settings-template-section__title-row {
    align-items: center;
    display: flex;
    justify-content: space-between;
    margin: 0 0 $spacing;
}
</style>
